/**
 * File a return on the portal — the whole flow, in one panel.
 * @file src/web/features/admin/components/FileReturnPanel.tsx
 *
 * Two OTPs bracket this, both to the client's own phone, because GSTN
 * requires a person to authorise a return. The CA rings the client once, and
 * everything between the two codes runs without them.
 *
 * The steps are shown as a list rather than a hidden wizard so a CA can see
 * where a filing stopped and resume, which is what actually happens when a
 * client does not pick up. Save, proceed and summary are re-runnable; the
 * final submit is not, and is styled and confirmed accordingly.
 */

import { useState, useRef, useEffect } from 'react';
import { Loader2, Check, Send, ArrowUp, UserCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { adminFetch, adminFetchJson } from '../lib/admin-api';
import { typedOtp, isOtp } from '../../../shared/utils/otp';
import { PortalSummary, SECTION_NAME, type PortalRow } from './PortalSummary';
import { focusPortalSession, type SessionInfo } from './PortalSessionPanel';

type StepKey = 'save' | 'proceed' | 'summary' | 'evc';

/** Which steps the server will allow, and the one to run next. */
type FilingSequence = {
  savedAt: string | null; proceededAt: string | null;
  canProceed: boolean; canSummary: boolean; canFile: boolean;
  next: 'save' | 'proceed' | 'summary' | 'file';
};

/** GSTN's own state for the period, as the server read it from the portal. */
type PortalHold = {
  readAt: string;
  chksum: string;
  sections: number;
  rows: PortalRow[];
  payload?: unknown;
  totals: { records: number; taxable: number; value: number; igst: number; cgst: number; sgst: number; cess: number } | null;
};

const inr = (n: number) => `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const DERIVED = /^TTL_LIAB$|_(4A|4B|6C|SEZWOP|SEZWP|B2B|B2C|DE|REG|UNREG)$/;
const documentRows = (p: PortalHold) => p.rows.filter((r) => !DERIVED.test(r.section));

/* Not everything GSTN counts is a document: Table 12 counts HSN lines and
   Table 13 counts document series. Calling all three "docs" made the upload
   line read as five documents for a two-invoice month. */
const UNIT: Record<string, [string, string]> = {
  HSN: ['row', 'rows'],
  DOC_ISSUE: ['series', 'series'],
};
const countOf = (section: string, n: number) => {
  const [one, many] = UNIT[section] ?? ['doc', 'docs'];
  return `${n} ${n === 1 ? one : many}`;
};

/**
 * What GSTN holds, said under the step it is about.
 *
 * One block at the foot of the panel made a CA read the whole thing to learn
 * whether the upload had landed. Each step answers for itself instead: the
 * upload says which sections GSTN has and for how much, proceed says the
 * summary is built, and the summary step shows the checksum the signature
 * will be checked against. These are GSTN's figures, never a copy of ours.
 */
function AtGstn({ portal, step }: { portal: PortalHold; step: StepKey }) {
  const rows = documentRows(portal);
  let body: React.ReactNode = null;

  if (step === 'save') {
    body = rows.length
      ? (
        <span className="flex flex-wrap gap-x-3 gap-y-0.5">
          {rows.map((r) => (
            <span key={r.section}>
              <span className="font-semibold">{SECTION_NAME[r.section] ?? r.section}</span>{' '}
              {countOf(r.section, r.records)}
              {r.taxable ? ` · ${inr(r.taxable)}` : ''}
              {r.tax ? ` · tax ${inr(r.tax)}` : ''}
            </span>
          ))}
        </span>
      )
      : <span>nothing saved for this period yet</span>;
  } else if (step === 'proceed') {
    body = <span>summary built and the return marked ready to file — that is why this cannot be run again</span>;
  } else if (step === 'summary') {
    body = (
      <span>
        {portal.sections} sections · read at {new Date(portal.readAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
        <span className="block break-all font-mono text-[10px] opacity-80">chksum {portal.chksum}</span>
      </span>
    );
  }
  if (!body) return null;

  return (
    <span className="mt-1 flex items-start gap-1.5 rounded-md bg-ok-50 px-2 py-1 text-[11px] text-ok-800">
      <Check className="mt-px h-3 w-3 shrink-0 text-ok-600" />
      <span className="min-w-0">
        <span className="font-semibold">At GSTN</span> · {body}
      </span>
    </span>
  );
}

/**
 * POST and read the answer whatever the status.
 *
 * The filing routes answer a refusal with 400 and a body that says what
 * happened — GSTN's own message, "already filed" with its ARN. adminFetchJson
 * throws on any non-2xx, which threw that body away: a refused file looked
 * like nothing at all. Only a reply with no readable body is an error here.
 */
async function postFiling(url: string, init: Parameters<typeof adminFetch>[1]): Promise<any> {
  const res = await adminFetch(url, { ...init, method: 'POST' });
  const body = await res.json().catch(() => null);
  if (body && typeof body === 'object') return body;
  if (!res.ok) throw new Error(`The server answered ${res.status}${res.statusText ? ` ${res.statusText}` : ''} with nothing readable.`);
  return {};
}

/** GSTN's words for an OTP that will not do — the only case to send another. */
const OTP_REFUSED = /otp|one[-\s]?time|invalid code|expired/i;

/* GSTN's filing errors, which are never about the OTP however they are worded.
 * RT_FIL_09 ("Signed summary is not the latest one") was being shown as "GSTN
 * refused the OTP — send a new one", and sent a CA to the client for a second
 * code that could not have helped: the code it was signed with had already
 * been accepted. An error code outranks any guess made from the message. */
const NOT_OTP_CODE = /^RT_FIL_|^RET\d/i;

/** Fired when this panel learns a return is filed, so the history re-reads. */
export const GST_FILED_EVENT = 'oddits:gst-filed';
const announceFiled = () => window.dispatchEvent(new Event(GST_FILED_EVENT));
export type ReturnType = 'GSTR1' | 'GSTR3B';
const LABEL: Record<ReturnType, string> = { GSTR1: 'GSTR-1', GSTR3B: 'GSTR-3B' };

/* GSTR-3B has no `proceed`. GSTN computes the liability and the taxpayer
   offsets it against the cash and credit ledgers — a payment, and one this app
   deliberately leaves on the portal. So 3B runs save → summary → EVC → file,
   with the offset done there in between. */
const stepsFor = (rt: ReturnType): { key: StepKey; label: string; detail: string }[] => [
  { key: 'save', label: 'Upload return', detail: `Sends the ${LABEL[rt]} we built to the portal. Re-runnable — saving twice overwrites.` },
  /* The GSP's own order, and its own words: new-proceed "generates the summary
     and marks the return ready for filing". Save and proceed are each
     asynchronous — they return a reference_id and GSTN works on it for a
     minute or two — so each waits for its own completion before the next can
     see anything. Reading the summary early is what answers "Latest Summary is
     not available". */
  ...(rt === 'GSTR1'
    ? [{ key: 'proceed' as StepKey, label: 'Generate summary & proceed', detail: 'GSTN computes its own figures and marks the return ready. It works in the background, so this waits.' }]
    : []),
  { key: 'summary', label: 'Fetch portal summary', detail: "GSTN's own figures and checksums — confirm against these, not ours. Re-run any time; filing always signs the latest." },
  /* 'evc' is deliberately NOT here. It was listed under "no client needed",
     which is exactly what it is not: it rings the client's phone, the code
     lasts about ten minutes, and sending it from up here left a CA scrolling
     to the File button while it expired. It belongs beside the signature, and
     that is where it is now rendered. */
];

/** GSTN's way of saying "not built yet" — the one error worth waiting out
 *  rather than reporting. */
const RETRYABLE = /summary is not available|generate summary|summary not generated|no summary|still processing/i;

export function FileReturnPanel({ entityId, gstRegistrationId, period, filingId, returnType = 'GSTR1', session, fileBlocked = null, part, onPortalState }: {
  entityId: string | null; gstRegistrationId?: string | null; period: string; filingId?: string | null;
  returnType?: ReturnType;
  /** This card's place in the page's numbered sequence, e.g. "4b". */
  part?: string;
  /** Told whenever the portal's own state is read, so the card above can lock
   *  its own button against the same fact instead of asking GSTN twice. */
  onPortalState?: (held: boolean) => void;
  /** Why filing must wait (e.g. 2B bills still to decide). The other steps still run. */
  fileBlocked?: string | null;
  /** The screen's one session, opened at the top. This panel reads it and
   *  never asks for a second OTP: GSTN grants six hours to the TAXPAYER, and
   *  the 2B fetch and both returns spend the same one. */
  session: SessionInfo | null;
}) {
  const name = LABEL[returnType];
  const path = returnType === 'GSTR3B' ? 'gstr3b' : 'gstr1';
  const STEPS = stepsFor(returnType);
  const info = session;
  const [busy, setBusy] = useState<string | null>(null);
  const [evcOtp, setEvcOtp] = useState('');
  const [done, setDone] = useState<Partial<Record<StepKey, boolean>>>({});
  const [summary, setSummary] = useState<unknown>(null);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);
  const [arn, setArn] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  /* What the last File press is doing, shown in step 6 itself — where the
     person is looking — not at the top of the panel. */
  const [filing, setFiling] = useState<null | { state: 'sending' } | { state: 'refreshed'; text: string } | { state: 'error'; text: string; resend: boolean; stop?: boolean }>(null);
  const confirmRef = useRef<HTMLDivElement>(null);
  const filingRef = useRef<HTMLDivElement>(null);
  /* Where the return stands, kept on the server so a reload does not reset it:
     already filed (from the portal's track list), and when the EVC OTP went
     out and to whose PAN. */
  const [filed, setFiled] = useState<{ arn: string | null; filedOn: string | null } | null>(null);
  const [evc, setEvc] = useState<{ sentAt: string; pan: string | null } | null>(null);
  /* What GSTN itself holds, read from the portal each time this opens.
   *
   * The ticks below are only this app's memory of its own run — a reload
   * forgets them, and they never said anything about the portal. Once GSTN
   * holds a built summary, uploading again throws it away and sends the
   * return back for a rebuild it will not accept a signature against for some
   * minutes; that has already cost one filing day. So the portal's answer, not
   * ours, decides whether steps 2 and 3 can be pressed at all. */
  const [portal, setPortal] = useState<PortalHold | null>(null);
  /* Which steps the SERVER says may run, from the stamps it keeps: when the
     data last went up and when GSTN last built its summary over it. The ticks
     below are this page's memory and a reload forgets them; this does not,
     and it is what the server enforces anyway. */
  const [seq, setSeq] = useState<FilingSequence | null>(null);
  const [tab, setTab] = useState<'filing' | 'log'>('filing');
  /* Deliberately pressed when the books really have changed, since a CA who
     edits an invoice must be able to re-upload without destroying anything. */
  const [reupload, setReupload] = useState(false);
  const [resetting, setResetting] = useState<'ask' | 'running' | null>(null);
  /* GSTN builds the summary asynchronously — a minute or two is normal. A bare
     failure here reads as "something is wrong" when the honest answer is "not
     yet", so the wait is shown and counted rather than handed back. */
  const [waiting, setWaiting] = useState<{ left: number; attempt: number; of: number } | null>(null);
  const stop = useRef(false);

  /* The EVC is the authorised signatory's signature: its OTP goes to the
     mobile linked to their PAN, and the file call carries the same PAN. That
     is the signatory recorded in Masters, or a proprietor's own PAN from the
     GSTIN. For anyone else with nobody recorded, both steps wait for one —
     the GSP refused the request outright without it. */
  const signatory = info?.signatory ?? null;

  const q = { entityId: entityId ?? undefined, gstRegistrationId: gstRegistrationId ?? undefined, period };


  async function run(name: string, fn: () => Promise<any>) {
    setBusy(name); setMsg(null);
    try {
      const r = await fn();
      if (r?.ok === false) {
        // The portal says this period is closed: the panel switches to that state.
        if (r.alreadyFiled) { setFiled({ arn: r.arn ?? null, filedOn: r.filedOn ?? null }); setConfirming(false); announceFiled(); }
        const text = r.error ?? 'Failed';
        /* GSTN had rebuilt its summary; the server fetched it again. Nothing
           was filed and the OTP is still good — press File again. */
        if (name === 'submit' && r.summaryRefreshed) {
          setDone((d) => ({ ...d, summary: true }));
          setFiling({ state: 'refreshed', text }); // said in step 6, where File is
          return null;
        }
        setMsg({ tone: 'err', text });
        /* `needsRegenerate` is the refusal with a way out: the summary GSTN
           holds is not the one it calls latest, and only a rebuild fixes that.
           `payloadMismatch` is kept so an older server still renders. */
        if (name === 'submit') {
          const rebuild = !!r.needsRegenerate || !!r.payloadMismatch;
          /* A filing error code settles it. Without this, "Signed summary is
             not the latest one" was read as an OTP failure — because the word
             "latest" is not in the pattern but "expired or incorrect" wording
             from elsewhere was — and the CA was told to ring the client for
             another code that could not have changed the outcome. */
          const otpFault = !NOT_OTP_CODE.test(String(r.code ?? '')) && OTP_REFUSED.test(text);
          setFiling({ state: 'error', text, resend: !r.alreadyFiled && !rebuild && otpFault, stop: rebuild });
        }
        return null;
      }
      return r;
    } catch (e) {
      const text = e instanceof Error ? e.message.replace(/^\d+\s/, '') : 'Failed';
      setMsg({ tone: 'err', text });
      if (name === 'submit') setFiling({ state: 'error', text, resend: false });
      return null;
    } finally { setBusy(null); }
  }

  const once = (k: StepKey, extra?: Record<string, string>) =>
    postFiling(`/api/gst/file/${path}/${k}`, { query: { ...q, ...extra } });

  /* One section at a time, when the portal refuses the whole return without
     saying which part it dislikes. Nothing is filed by a save. */
  const onlySection = (section: string) => run('save', async () => {
    const r = await once('save', { only: section });
    setMsg(r?.ok === false
      ? { tone: 'err', text: `${section} alone: ${r.error}` }
      : { tone: 'ok', text: `${section} alone was accepted — the problem is in another section.` });
    return r;
  });

  const step = (k: StepKey) => run(k, async () => {
    let r = await once(k);

    /* Only the summary waits, and only for the one error that means "not yet".
       Twelve attempts twenty seconds apart is four minutes — longer than GSTN
       has ever taken for a month of this size, and short enough that a real
       failure is not mistaken for patience. */
    if ((k === 'summary' || k === 'proceed' || k === 'save') && r?.ok === false && RETRYABLE.test(String(r.error ?? ''))) {
      stop.current = false;
      const of = 12;
      for (let attempt = 1; attempt <= of && !stop.current; attempt++) {
        for (let left = 20; left > 0 && !stop.current; left--) {
          setWaiting({ left, attempt, of });
          await new Promise((res) => setTimeout(res, 1000));
        }
        if (stop.current) break;
        r = await once(k);
        if (r?.ok !== false || !RETRYABLE.test(String(r.error ?? ''))) break;
      }
      setWaiting(null);
      if (stop.current) return { ok: false, error: 'Stopped waiting. GSTN had not built the summary yet — run this again when you are ready.' };
    }

    if (r?.ok !== false) {
      /* The server moved the sequence on, so read it back rather than infer
         it here. Two places deciding what may be pressed next is how they
         came to disagree in the first place. */
      void refreshSequence();
      // A new upload or proceed makes GSTN build a new summary: the summary
      // and the OTP sent against the old one no longer count.
      setDone((d) => (k === 'save' || k === 'proceed' ? { ...d, [k]: true, summary: false, evc: false } : { ...d, [k]: true }));
      if (k === 'save' || k === 'proceed') { setSummary(null); setEvc(null); setEvcOtp(''); }
      if (k === 'evc') { setEvc({ sentAt: r.evcSentAt ?? new Date().toISOString(), pan: r.evcPan ?? null }); setEvcOtp(''); }
      if (k === 'summary') setSummary(r.data);
    }
    return r;
  });

  /* File. The request, and every outcome of it, is shown in step 6: a spinner
     while it runs, then the ARN or GSTN's own refusal in red. The OTP stays in
     the box whatever happens short of success — it is cleared only once filed. */
  /* Rebuild the summary, then read back what the rebuild produced.
   *
   * Proceed-to-file is what makes GSTN build a new one; fetching only reads
   * whatever stands. After a filing refused as "not the latest", those two
   * have to happen in that order — and then the signature has to be fresh, so
   * the OTP box is cleared rather than left holding a code for a summary that
   * no longer exists. */
  /* Clear the period at GSTN. Destructive, confirmed above, and never
     automatic — it is offered only as a way out of a summary GSTN has marked
     ready and filing keeps refusing. Afterwards the portal holds nothing, so
     every step opens again and the return is uploaded from scratch. */
  const resetPortal = async () => {
    setResetting('running');
    const r = await run('reset', () => postFiling('/api/gst/portal/reset', { query: q, body: JSON.stringify({ period }) }));
    setResetting(null);
    if (r?.ok === false) return;
    setPortal(null);
    onPortalState?.(false);
    setReupload(false);
    setSummary(null);
    setEvc(null);
    setEvcOtp('');
    setDone({});
    setFiling(null);
    setMsg({ tone: 'ok', text: 'The portal is cleared for this period. Upload the return again, then proceed, wait for GSTN to build the summary, and sign it.' });
  };

  const regenerate = () => run('regenerate', async () => {
    const p = await once('proceed');
    if (p?.ok === false) { setMsg({ tone: 'err', text: `Could not regenerate: ${p.error}` }); return p; }
    const sum = await once('summary');
    if (sum?.ok === false) { setMsg({ tone: 'err', text: `Regenerated, but the summary did not come back: ${sum.error}` }); return sum; }
    setSummary(sum.data);
    setDone((d) => ({ ...d, proceed: true, summary: true }));
    setEvcOtp('');
    setMsg({ tone: 'ok', text: 'The portal rebuilt its summary. Send a fresh EVC OTP, then file.' });
    return sum;
  });


  const submit = async () => {
    if (busy) return;
    setFiling({ state: 'sending' });
    const r = await run('submit', () => postFiling(`/api/gst/file/${path}/submit`, {
      query: q,
      // The summary is kept on the server too, so this works after a reload.
      body: JSON.stringify({ otp: evcOtp, ...(summary ? { summary } : {}), filingId: filingId ?? null }),
    }));
    setConfirming(false);
    if (r?.arn) { setArn(r.arn); setEvcOtp(''); setFiling(null); announceFiled(); return; }
    // run() kept the message; show it here too, with a resend only when GSTN
    // says it is the OTP that is wrong.
    setFiling((f) => (f?.state === 'error' || f?.state === 'refreshed' ? f : { state: 'error', text: 'The portal did not confirm the filing. Check the return status before trying again.', resend: false }));
  };

  /* One answer to "is this filed", whichever way it got there: our own
     filing this session, or the portal's word on opening. The panel used to
     treat them as two states and show the whole six-step apparatus for the
     second — and call a return we had just filed "filed (outside oddits)". */
  const doneFiled = { filedArn: arn ?? filed?.arn ?? (filed ? '' : null) };
  const filedOn = filed?.filedOn ?? null;

  const live = !!info?.session;

  /* Where the sequence stands, asked of the server. Cheap — it reads stamps,
     and only touches the portal when a session is open. */
  const refreshSequence = async () => {
    if (!entityId || !/^\d{6}$/.test(period)) return;
    try {
      const st = await adminFetchJson<any>(`/api/gst/file/${path}/state`, { query: q });
      if (st?.ok === false) return;
      setSeq(st.sequence ?? null);
      setPortal(st.portal ?? null);
      onPortalState?.(!!st.portal);
    } catch { /* the buttons still work; they just judge from this page */ }
  };

  /* On opening, and again when a session opens: has this return been filed
     (asked of the portal when there is a session), and how far did the steps
     get before the last reload. */
  useEffect(() => {
    if (!entityId || !/^\d{6}$/.test(period)) return;
    let gone = false;
    adminFetchJson<any>(`/api/gst/file/${path}/state`, { query: q })
      .then((st) => {
        if (gone || st?.ok === false) return;
        setFiled(st.filed ?? null);
        setPortal(st.portal ?? null);
        setSeq(st.sequence ?? null);
        onPortalState?.(!!st.portal);
        if (st.portal) setReupload(false);
        if (st.filed) announceFiled();
        setDone((d) => ({ ...d, summary: !!st.summaryFetched, evc: !!st.evcSentAt }));
        setEvc(st.evcSentAt ? { sentAt: st.evcSentAt, pan: st.evcPan ?? null } : null);
      })
      .catch(() => { /* the steps still work; they just start from the top */ });
    return () => { gone = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, gstRegistrationId, period, path, live]);

  /* The EVC OTP is valid for about ten minutes. Past that it is said, not
     enforced — GSTN is the judge, and its answer is shown if it refuses. */
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!evc) return;
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, [evc]);
  const evcAgeMin = evc ? Math.floor((now - new Date(evc.sentAt).getTime()) / 60_000) : 0;
  const sentTime = evc ? new Date(evc.sentAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '';
  const otpOk = isOtp(evcOtp);
  /* The server's answer wins over this page's tick: a stale tab can believe a
   summary was fetched for data that has since been replaced. */
  const summaryCurrent = seq ? seq.canFile : !!done.summary;
  const canFile = live && !filed && !fileBlocked && !!signatory && summaryCurrent && !!evc && otpOk && !busy;

  /* The confirmation and the outcome are brought into view when they appear:
     on a long page step 6 can sit below the fold, and an answer nobody sees is
     the same as none. */
  useEffect(() => {
    if (!confirming) return;
    confirmRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
    confirmRef.current?.querySelector<HTMLButtonElement>('button[data-confirm]')?.focus();
  }, [confirming]);
  useEffect(() => {
    if (filing) filingRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  }, [filing]);
  const configured = info?.configured !== false;

  return (
    <div className="space-y-4 rounded-2xl border border-ink-200 bg-white p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-ink-900">
            {part && <span className="mr-1.5 font-mono text-[11px] text-ink-400">{part}</span>}
            File on the GST portal
          </h3>
          <p className="text-xs text-ink-500">{name} · GSTIN {info?.gstin ?? '—'} · period {period}</p>
        </div>
        {/* The log is a tab, not a disclosure buried under the panel: it is
            opened when something has to be explained and ignored otherwise. */}
        <div className="flex shrink-0 rounded-full border border-ink-200 p-0.5 text-[11px] font-semibold">
          {([['filing', 'Filing'], ['log', 'Submission log']] as const).map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`rounded-full px-3 py-1 transition ${tab === k ? 'bg-inkblack text-paper' : 'text-ink-500 hover:text-ink-800'}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {tab === 'log' ? <SubmissionLog period={period} q={q} /> : (<>

      {/* ── 1. taxpayer session — opened once at the top of the screen ──
          Two OTP forms on one page is one too many: they share a session, so
          the second would either duplicate the first or contradict it. This
          says where the flow stands and leaves the asking to the header. */}
      <div className={`rounded-xl border px-4 py-3 text-xs ${live ? 'border-ok-200 bg-ok-50 text-ok-700' : 'border-warn-200 bg-warn-50 text-warn-700'}`}>
        {live ? (
          <>Step 1 — portal session open{info?.session ? `, ${info.session.minutesLeft} min left` : ''}. The steps below run without the client.</>
        ) : (
          <span className="flex flex-wrap items-center gap-2">
            <span>Step 1 — no portal session. One code from the client covers every step below for six hours.</span>
            <button
              onClick={focusPortalSession}
              className="inline-flex items-center gap-1 rounded-lg border border-warn-300 bg-white px-2 py-0.5 text-[11px] font-semibold text-warn-800 hover:bg-warn-50"
            >
              <ArrowUp className="h-3 w-3" /> Open a session
            </button>
          </span>
        )}
      </div>

      {!configured && (
        <p className="rounded-xl border border-ink-200 bg-ink-50/60 px-4 py-3 text-xs text-ink-600">
          Until those credentials exist, download the JSON above and upload it on the portal by hand.
        </p>
      )}

      {msg?.tone === 'err' && /structure|RET191106/i.test(msg.text) && (
        <div className="rounded-xl border border-ink-200 bg-ink-50/70 px-4 py-3 text-xs text-ink-600">
          <p className="font-semibold text-ink-800">The portal will not say which section it refused.</p>
          <p className="mt-0.5">Offer it one at a time — nothing is filed, and the full return goes up afterwards.</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {['b2b', 'b2cs', 'hsn', 'doc_issue'].map((sec) => (
              <button key={sec} onClick={() => onlySection(sec)} disabled={!live || !!busy || !!filed}
                className="rounded-full border border-ink-300 bg-white px-2.5 py-1 font-mono text-[11px] font-semibold text-ink-700 hover:bg-ink-50 disabled:opacity-40">
                {sec}
              </button>
            ))}
          </div>
        </div>
      )}

      {msg && (
        <div className={`rounded-xl border px-4 py-2.5 text-xs ${msg.tone === 'ok' ? 'border-ok-200 bg-ok-50 text-ok-700' : 'border-danger-200 bg-danger-50 text-danger-700'}`}>
          {msg.text}
        </div>
      )}

      {/* ── filed: the whole panel collapses to this ──
          Six steps, a locked OTP box and an irreversible red button are
          scaffolding for a job already done. Once GSTN holds the return there
          is one thing left to want — the copy GSTN itself holds — so that is
          all this shows. */}
      {doneFiled.filedArn !== null ? (
        <div className="space-y-3">
          <div className="rounded-xl border border-ok-200 bg-ok-50 px-4 py-4 text-sm text-ok-800">
            <p className="flex items-center gap-2 font-semibold">
              <Check className="h-4 w-4" /> Filed{filedOn ? ` on ${filedOn}` : ''}.
            </p>
            <p className="mt-1">
              ARN <span className="font-mono">{doneFiled.filedArn || '—'}</span> — recorded against {period.slice(0, 2)}/{period.slice(2)}.
            </p>
            <p className="mt-1 text-xs text-ok-700">
              A correction to a filed return goes in as an amendment in a later period; this one cannot be changed.
            </p>
          </div>
          <FiledCopy path={path} period={period} q={q} name={name} gstin={info?.gstin ?? null} />
        </div>
      ) : (
        <>
          {/* ── 2-5. the automatic part ── */}
          <div className="rounded-xl border border-ink-200 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-ink-500">
              Steps 2–4 — no client needed{' '}
              <span className="font-normal normal-case tracking-normal text-ink-400">· the client is only needed for the OTP below</span>
            </p>
            <ul className="mt-3 space-y-2">
              {STEPS.map((s) => {
                const needsSignatory = s.key === 'evc' && !signatory;
                /* Locked while GSTN holds a built summary. Uploading replaces
                   it; proceeding is refused outright ("Return Form already
                   ready to be filed"). Neither is a step to leave pressable
                   next to a return that is ready to sign. */
                const heldByPortal = !!portal && (s.key === 'save' || s.key === 'proceed') && !(s.key === 'save' && reupload);
                /* Out of turn: the server refuses these anyway, so the button
                   says so instead of spending a call to find out. The upload
                   is never out of turn — it starts the sequence. */
                const tooEarly = !!seq && ((s.key === 'proceed' && !seq.canProceed) || (s.key === 'summary' && !seq.canSummary));
                const waitingFor = tooEarly ? (s.key === 'proceed' ? 'Upload return' : 'Generate summary & proceed') : null;
                return (
                <li key={s.key} className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <span className="text-sm font-medium text-ink-800">{s.label}</span>
                    <span className="block text-[11px] text-ink-400">
                      {heldByPortal && s.key === 'save'
                        ? 'Locked: uploading again replaces what GSTN holds and sends the return back for a rebuild.'
                        : tooEarly
                          ? `Waits for “${waitingFor}”. Running it now would build a summary over data the portal does not have yet — which GSTN then refuses to file against.`
                          : s.detail}
                    </span>
                    {portal && <AtGstn portal={portal} step={s.key} />}
                  </div>
                  {waiting && busy === s.key ? (
                    <span className="shrink-0 inline-flex items-center gap-2 rounded-full border border-ink-300 bg-ink-50 px-3 py-1.5 text-[11px] font-semibold text-ink-600">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      GSTN is building it — retrying in {waiting.left}s ({waiting.attempt}/{waiting.of})
                      <button onClick={() => { stop.current = true; }} className="underline hover:text-ink-900">stop</button>
                    </span>
                  ) : (
                    <button onClick={() => step(s.key)} disabled={!live || !!busy || needsSignatory || !!filed || heldByPortal || tooEarly}
                      title={needsSignatory
                        ? 'Add the authorized signatory (name + PAN) in Masters first'
                        : heldByPortal ? 'GSTN already holds this return — see below'
                          : tooEarly ? `Run “${waitingFor}” first` : undefined}
                      className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-ink-300 bg-white px-3 py-1.5 text-[11px] font-semibold text-ink-700 hover:bg-ink-50 disabled:opacity-40">
                      {busy === s.key && <Loader2 className="h-3 w-3 animate-spin" />}
                      {/* The summary can be fetched again at any time — GSTN rebuilds
                          it, and filing always signs the latest. */}
                      {/* The line beneath already says what GSTN holds, so the
                          button says only why it cannot be pressed. */}
                      {tooEarly ? <>Waits</> : heldByPortal ? (s.key === 'proceed'
                        ? <><Check className="h-3 w-3 text-ok-600" /> Done</>
                        : <>Locked</>) : done[s.key] ? (s.key === 'summary'
                        ? <><Check className="h-3 w-3 text-ok-600" /> Fetched · Refresh</>
                        : <><Check className="h-3 w-3 text-ok-600" /> Done</>) : 'Run'}
                    </button>
                  )}
                </li>
                );
              })}
            </ul>

            {/* GSTN's own state, in GSTN's own figures.
                Not a copy of ours: this is what the portal answered when the
                panel opened, and it is the thing the signature is checked
                against. A CA comparing it with the return above can see at a
                glance whether the portal is holding what they mean to file. */}
            {portal && (
              <div className="mt-3 rounded-lg border border-ok-200 bg-ok-50 px-3 py-2.5 text-xs text-ok-800">
                <p className="font-semibold">The portal is holding this return — ready to sign.</p>
                {portal.totals && (
                  <p className="mt-1 text-ok-700">
                    GSTN&rsquo;s total: taxable {inr(portal.totals.taxable)} · tax{' '}
                    {inr(portal.totals.igst + portal.totals.cgst + portal.totals.sgst + portal.totals.cess)} · invoice value {inr(portal.totals.value)}.
                    {' '}Compare it with the return above — these are GSTN&rsquo;s figures, not ours.
                  </p>
                )}
                <p className="mt-1 text-ok-700">
                  Nothing to upload again. Read the summary below, then sign and file.
                </p>

                {/* Two ways out, and they are not the same thing.
                    Re-uploading is ordinary and non-destructive: GSTN overwrites
                    what it holds, which is what a corrected invoice needs.
                    Resetting throws the portal's own copy away — the
                    auto-populated e-invoice rows with it — and exists only for a
                    summary GSTN has marked ready and filing keeps refusing.

                    Saying the books have moved unlocks the check above as well
                    as the upload: it is one decision, and re-checking is what
                    you do before re-uploading. */}
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  {!reupload && (
                    <button onClick={() => { setReupload(true); onPortalState?.(false); }} disabled={!!busy}
                      className="text-[11px] font-semibold text-ok-800 underline underline-offset-2 disabled:opacity-40">
                      The books changed — let me upload again
                    </button>
                  )}
                  <button onClick={() => setResetting('ask')} disabled={!!busy || resetting === 'running'}
                    className="text-[11px] font-semibold text-danger-700 underline underline-offset-2 disabled:opacity-40">
                    Clear it on the portal
                  </button>
                </div>
              </div>
            )}

            {/* The summary GSTN last generated — read, printed and handed over
                as it came back. Shown here, under the steps that produced it
                and above the signature that commits to it, because this is
                the last thing anyone should look at before filing. */}
            {portal && (
              <PortalSummary
                gstin={info?.gstin ?? null} fp={period}
                rows={portal.rows} chksum={portal.chksum} readAt={portal.readAt}
                payload={portal.payload ?? summary ?? undefined}
              />
            )}

            {reupload && portal && (
              <p className="mt-2 rounded-lg border border-warn-200 bg-warn-50 px-3 py-2 text-[11px] text-warn-800">
                Upload is unlocked. It replaces what GSTN holds and sends the return back for a rebuild — give the portal a few
                minutes after step 3 before signing, or it refuses the signature as &ldquo;not the latest&rdquo;.
              </p>
            )}

            {resetting && (
              <div className="mt-2 rounded-lg border border-danger-300 bg-danger-50 px-3 py-2.5 text-xs text-danger-800">
                <p className="font-semibold">Clear {period.slice(0, 2)}/{period.slice(2)} on the GST portal?</p>
                <p className="mt-1">
                  This deletes what GSTN has <span className="font-semibold">saved</span> for this period — including the e-invoice rows
                  GSTN auto-populated from the IRP — and the summary with it. Nothing filed is touched, and nothing in oddits is
                  touched: you would upload again from here. Use it only when a return GSTN calls ready keeps refusing your signature.
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <button onClick={resetPortal} disabled={resetting === 'running'}
                    className="inline-flex items-center gap-1.5 rounded-full bg-danger-600 px-3 py-1.5 text-[11px] font-semibold text-white disabled:opacity-40">
                    {resetting === 'running' && <Loader2 className="h-3 w-3 animate-spin" />}
                    Yes, clear it on the portal
                  </button>
                  <button onClick={() => setResetting(null)} disabled={resetting === 'running'}
                    className="rounded-full border border-ink-300 bg-white px-3 py-1.5 text-[11px] font-semibold text-ink-700 disabled:opacity-40">
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Who signs — or what to do when nobody is recorded. */}
            {signatory ? (
              <p className="mt-3 flex items-start gap-1.5 text-[11px] text-ink-500">
                <UserCheck className="mt-px h-3.5 w-3.5 shrink-0 text-ok-600" />
                <span>
                  The EVC OTP goes to the mobile linked to {signatory.name}&rsquo;s PAN ({signatory.pan})
                  {signatory.fromGstin && <>, the proprietor&rsquo;s own, from the GSTIN. If someone else signs, <Link to="/admin/masters?tab=company" className="underline">add them in Masters</Link></>}.
                </span>
              </p>
            ) : (
              <div className="mt-3 rounded-lg border border-warn-200 bg-warn-50 px-3 py-2.5 text-xs text-warn-700">
                <p>
                  Add the authorized signatory (name + PAN) in Masters before sending the EVC OTP.{' '}
                  <Link to="/admin/masters?tab=company" className="font-semibold underline">Open Masters</Link>
                </p>
              </div>
            )}
          </div>

          {/* ── 6. the irreversible one ── */}
          <div className="rounded-xl border border-danger-200 bg-danger-50/40 p-4">
            <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-danger-700">
              <Send className="h-3.5 w-3.5" /> Steps 5 &amp; 6 — sign and file
            </p>
            <p className="mt-1 text-[11px] text-danger-700">
              Irreversible. Once GSTN accepts this, the return can only be corrected as an amendment in a later period.
              {returnType === 'GSTR3B' && ' A 3B can only be filed once its liability is settled — offset it against the cash and credit ledgers on the portal first.'}
            </p>
            {fileBlocked && !filed && (
              <p role="alert" className="mt-3 rounded-lg border border-danger-300 bg-white px-3 py-2 text-xs font-semibold text-danger-800">{fileBlocked}</p>
            )}
            {filed ? (
              <p className="mt-3 text-[11px] text-ink-500">Nothing to file — the portal already has this return.</p>
            ) : !evc ? (
              /* The OTP is sent from here, not from the list above.
                 It lasts about ten minutes, so the send and the box that takes
                 the code have to be within sight of the File button — ring the
                 client, send it, type it, file. */
              <div className="mt-3 space-y-2">
                <p className="text-[11px] text-ink-600">
                  Everything else is done. This sends the client a 6-character code for the signature — it lasts about ten minutes, so have them on the phone.
                </p>
                <button type="button" onClick={() => step('evc')} disabled={!live || !!busy || !signatory || !summaryCurrent}
                  title={!signatory ? 'Add the authorized signatory (name + PAN) in Masters first' : !summaryCurrent ? 'Fetch the portal summary first' : undefined}
                  className="inline-flex items-center gap-1.5 rounded-full bg-inkblack px-4 py-2 text-xs font-semibold text-paper disabled:opacity-40">
                  {busy === 'evc' && <Loader2 className="h-3 w-3 animate-spin" />}
                  Send EVC OTP
                </button>
                {!signatory && (
                  <p className="text-[11px] text-warn-700">
                    Add the authorized signatory (name + PAN) in Masters first.{' '}
                    <Link to="/admin/masters?tab=company" className="font-semibold underline">Open Masters</Link>
                  </p>
                )}
              </div>
            ) : (
              <div className="mt-3 space-y-2">
                <p className="text-xs text-ink-700">
                  OTP sent to {evc.pan ? `${evc.pan}'s` : "the signatory's"} mobile at {sentTime}.{' '}
                  <span className={evcAgeMin >= 10 ? 'font-semibold text-danger-700' : 'text-ink-500'}>
                    {evcAgeMin >= 10 ? 'It has probably expired (they last about 10 minutes) — resend it.' : 'It expires in about 10 minutes.'}
                  </span>
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  {/* Letters and digits: an EVC OTP is not always a number, so no
                      numeric keypad and nothing stripped but spaces and symbols. */}
                  <input value={evcOtp} autoComplete="one-time-code" autoCapitalize="characters" spellCheck={false} maxLength={6} placeholder="6-character OTP"
                    aria-label="EVC OTP"
                    onChange={(e) => { setEvcOtp(typedOtp(e.target.value)); setConfirming(false); }}
                    className="w-36 rounded-lg border border-danger-300 bg-white px-3 py-2 text-sm tracking-widest tabular-nums focus:outline-none" />
                  <button type="button" onClick={() => { setFiling(null); setConfirming(true); }} disabled={!canFile || confirming}
                    title={!done.summary ? 'Fetch the portal summary first' : !otpOk ? 'Enter the 6-character OTP' : undefined}
                    className="inline-flex items-center gap-1.5 rounded-full bg-danger-600 px-4 py-2 text-xs font-semibold text-white disabled:opacity-40">
                    File {name}
                  </button>
                  <button type="button" onClick={() => step('evc')} disabled={!live || !!busy || !signatory}
                    className="text-xs font-medium text-ink-600 underline hover:text-ink-900 disabled:opacity-40">
                    {busy === 'evc' ? 'Sending…' : 'Resend OTP'}
                  </button>
                </div>

                {/* The confirmation: in the panel, never a browser dialog —
                    a browser can suppress those without a word. */}
                {confirming && (
                  <div ref={confirmRef} role="alertdialog" aria-labelledby="file-confirm-title"
                    className="relative z-10 rounded-lg border-2 border-danger-400 bg-white p-3 shadow-sm">
                    <p id="file-confirm-title" className="text-sm font-semibold text-danger-800">
                      File {name} for {period.slice(0, 2)}/{period.slice(2)}? This cannot be undone.
                    </p>
                    <p className="mt-0.5 text-[11px] text-ink-600">GSTN files it under the OTP {evcOtp}. A mistake afterwards is corrected only by an amendment in a later period.</p>
                    <div className="mt-2.5 flex items-center gap-2">
                      <button type="button" data-confirm onClick={submit} disabled={!canFile}
                        className="inline-flex items-center gap-1.5 rounded-full bg-danger-600 px-4 py-2 text-xs font-semibold text-white disabled:opacity-50">
                        {busy === 'submit' && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Yes, file now
                      </button>
                      <button type="button" onClick={() => setConfirming(false)} disabled={busy === 'submit'}
                        className="rounded-full px-3 py-2 text-xs font-medium text-ink-600 hover:text-ink-900">Cancel</button>
                    </div>
                  </div>
                )}

                {/* What the press did. */}
                {filing && (
                  <div ref={filingRef} role="status" aria-live="polite">
                    {filing.state === 'refreshed' ? (
                      <p className="rounded-lg border border-ok-200 bg-ok-50 px-3 py-2 text-xs font-semibold text-ok-800">
                        {filing.text} <span className="font-normal">GSTN had rebuilt its summary since it was fetched; nothing was filed, and the OTP in the box is still good.</span>
                      </p>
                    ) : filing.state === 'sending' ? (
                      <p className="inline-flex items-center gap-2 rounded-lg border border-ink-200 bg-white px-3 py-2 text-xs text-ink-700">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Filing {name} with GSTN — this can take a minute. Do not close the page.
                      </p>
                    ) : (
                      <div className="rounded-lg border border-danger-300 bg-danger-50 px-3 py-2 text-xs text-danger-800">
                        <p className="font-semibold">Not filed.</p>
                        <p className="mt-0.5">{filing.text}</p>
                        {filing.stop
                          ? (
                            /* Not a dead end. GSTN calls a summary "the latest"
                               only while it is the one proceed-to-file built;
                               re-reading it cannot change that, which is why
                               the same OTP was refused twice against a freshly
                               fetched copy. Rebuilding it can — so offer that
                               rather than sending someone to support. */
                            <div className="mt-1 space-y-2">
                              <p>Re-reading the summary cannot help: GSTN counts only the one it last built as current, and both attempts signed a copy it had already replaced. <span className="font-semibold">Nothing has been filed.</span></p>
                              <p className="text-ink-700">Rebuild it on the portal, then sign the new one:</p>
                              <ol className="ml-4 list-decimal space-y-0.5 text-ink-700">
                                <li>Regenerate below — this re-runs step 4 and fetches what it produces</li>
                                <li>Send a fresh EVC OTP</li>
                                <li>File again</li>
                              </ol>
                              <button
                                onClick={regenerate}
                                disabled={!!busy}
                                className="inline-flex items-center gap-1.5 rounded-full bg-inkblack px-3 py-1.5 text-[11px] font-semibold text-paper disabled:opacity-40"
                              >
                                {busy === 'regenerate' && <Loader2 className="h-3 w-3 animate-spin" />}
                                Regenerate the summary
                              </button>
                            </div>
                          )
                          : filing.resend
                            ? <p className="mt-1">GSTN refused the OTP. Send a new one with <span className="font-semibold">Resend OTP</span>, then file again.</p>
                            : <p className="mt-1 text-ink-600">The OTP is still in the box — fix what GSTN names and press File again.</p>}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            {!filed && !done.summary && <p className="mt-2 text-[11px] text-ink-400">Fetch the portal summary first — filing uses GSTN&rsquo;s figures, not ours.</p>}
          </div>
        </>
      )}

      </>)}
    </div>
  );
}

/**
 * The portal trace, on the screen.
 *
 * Every request this app sent for the period and every answer GSTN gave, in
 * the order they happened. It had all been kept in the database already and
 * was readable nowhere — which is how a filing GSTN refused twice went a day
 * without an explanation, while the one sentence that explained it ("Return
 * Form already ready to be filed") was shown once and forgotten.
 *
 * Safe to read and to hand over: no OTP was ever written into these rows, and
 * the PAN in them is masked where it appears. Figures and checksums only.
 */
function SubmissionLog({ period, q }: { period: string; q: Record<string, string | undefined> }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [traces, setTraces] = useState<{ name: string; at: string; writtenAt: string; body: unknown }[] | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminFetch('/api/gst/portal/traces', { query: { ...q, period } });
      const body = await res.json().catch(() => null);
      if (!body || body.ok === false) { setError(body?.error ?? `The server answered ${res.status} with nothing readable.`); return; }
      setTraces(body.traces ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  /* Read once, when the tab is first opened — it is a portal call, and most
     visits to this panel never want it. */
  useEffect(() => { void load(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  /* Downloadable, because the next person who has to read one of these is as
     likely to be the GSP's support desk as the CA. */
  const download = () => {
    const url = URL.createObjectURL(new Blob([JSON.stringify(traces ?? [], null, 2)], { type: 'application/json' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `gst-submission-log-${period}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold text-ink-900">Submission log</span>
        <span className="text-[11px] text-ink-400">Everything sent to GSTN for {period.slice(0, 2)}/{period.slice(2)}, and everything it answered.</span>
        {traces && traces.length > 0 && (
          <>
            <button onClick={() => void load()} disabled={loading} className="text-[11px] font-semibold text-ink-600 underline underline-offset-2 disabled:opacity-40">Refresh</button>
            <button onClick={download} className="text-[11px] font-semibold text-ink-600 underline underline-offset-2">Download</button>
          </>
        )}
      </div>

      <div className="mt-3 space-y-2">
          {loading && <p className="inline-flex items-center gap-2 text-xs text-ink-500"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Reading the log…</p>}
          {error && <p className="rounded-lg border border-danger-300 bg-danger-50 px-3 py-2 text-xs text-danger-800">{error}</p>}
          {!loading && !error && traces?.length === 0 && (
          <p className="text-xs text-ink-500">Nothing yet for this period — the log fills as the steps run.</p>
          )}
        {traces?.map((t) => (
            <details key={t.name} className="rounded-lg border border-ink-200 bg-white">
              <summary className="cursor-pointer px-3 py-2 text-xs font-semibold text-ink-800">
                {t.name}
                <span className="ml-2 font-normal text-ink-400">{new Date(t.at).toLocaleString('en-IN')}</span>
              </summary>
              <pre className="max-h-96 overflow-auto border-t border-ink-100 bg-ink-50 px-3 py-2 text-[11px] leading-relaxed text-ink-800">
{JSON.stringify(t.body, null, 2)}
              </pre>
            </details>
        ))}
      </div>
    </div>
  );
}

/**
 * The filed return, as GSTN holds it.
 *
 * Not our copy of what we sent: this is read back from the portal after the
 * ARN, which is the only version that settles an argument later. Printed for
 * the client, spreadsheet for the file, JSON for whoever asks.
 *
 * Fetched on demand rather than on open, because it costs a portal call and
 * most visits to a filed period only want the ARN.
 */
function FiledCopy({ path, period, q, name, gstin }: {
  path: string; period: string; q: Record<string, string | undefined>; name: string; gstin: string | null;
}) {
  const [state, setState] = useState<{ loading: boolean; error?: string; portal?: any }>({ loading: false });

  const load = async () => {
    setState({ loading: true });
    try {
      // POST, as the route is declared — a GET here answered 404.
      const res = await adminFetch('/api/gst/portal/filed', {
        method: 'POST',
        query: { ...q, type: path === 'gstr3b' ? 'GSTR3B' : 'GSTR1' },
      });
      const body = await res.json().catch(() => null);
      if (!body || body.ok === false) {
        setState({ loading: false, error: body?.error ?? `The portal answered ${res.status} with nothing readable.` });
        return;
      }
      setState({ loading: false, portal: body.portal ?? null });
    } catch (e) {
      setState({ loading: false, error: e instanceof Error ? e.message : String(e) });
    }
  };

  const rows: PortalRow[] = (state.portal?.sec_sum ?? [])
    .filter((x: any) => x?.ttl_rec || x?.ttl_val || x?.ttl_tax)
    .map((x: any) => ({
      section: String(x.sec_nm),
      records: Number(x.ttl_rec ?? 0), taxable: Number(x.ttl_tax ?? 0), value: Number(x.ttl_val ?? 0),
      tax: Number(x.ttl_igst ?? 0) + Number(x.ttl_cgst ?? 0) + Number(x.ttl_sgst ?? 0) + Number(x.ttl_cess ?? 0),
    }));

  return (
    <div className="rounded-xl border border-ink-200 bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-ink-900">The filed {name}, from the portal</p>
          <p className="mt-0.5 text-xs text-ink-500">
            Read back from GSTN, not our copy of what we sent — the version that settles a question later.
          </p>
        </div>
        {!state.portal && (
          <button onClick={() => void load()} disabled={state.loading}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-ink-300 bg-white px-4 py-2 text-xs font-semibold text-ink-700 hover:bg-ink-50 disabled:opacity-40">
            {state.loading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Get it from the portal
          </button>
        )}
      </div>

      {state.error && (
        <p className="mt-3 rounded-lg border border-danger-300 bg-danger-50 px-3 py-2 text-xs text-danger-800">{state.error}</p>
      )}

      {state.portal && (rows.length ? (
        <PortalSummary
          gstin={gstin} fp={period} rows={rows}
          chksum={String(state.portal.chksum ?? '')}
          readAt={new Date().toISOString()}
          payload={state.portal}
          filed bare
        />
      ) : (
        <p className="mt-3 text-xs text-ink-500">The portal returned no section figures for this period.</p>
      ))}
    </div>
  );
}
